/**
 * RFC 9457 Problem Details, as the API returns them (`core.errors` on the backend).
 *
 *     { "type": "https://tendfolio.app/problems/conflict", "title": "Conflict",
 *       "status": 409, "detail": "A business named 'Cakes' already exists" }
 *
 * `code` is the last segment of the type URI — `conflict`, `entitlement-required`, `not-found` —
 * which is the stable identifier the UI branches on. Nothing should match on `title` or `detail`,
 * because those are prose and may change.
 */

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  /** Problem Details allows extra members; entitlement errors carry cap, used, plan and feature. */
  [member: string]: unknown;
}

export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.type.split("/").pop() ?? "";
  }

  /** The plan does not allow this. The UI offers an upgrade rather than reporting a failure. */
  get isEntitlement(): boolean {
    return this.code === "entitlement-required";
  }

  /** Nothing is paying for the workspace, so it refuses every change, whatever the plan's caps. */
  get isReadOnly(): boolean {
    return this.isEntitlement && this.member("feature") === "subscription";
  }

  get isConflict(): boolean {
    return this.code === "conflict";
  }

  /** A number the backend attached, such as the cap that was reached. */
  member(name: string): unknown {
    return this.problem[name];
  }

  static from(error: unknown, response: Response): ApiError {
    if (isProblem(error)) return new ApiError(error);
    // A proxy error, a crash, or an offline phone: nothing shaped like a problem came back.
    return new ApiError({
      type: "about:blank",
      title: response.statusText || "Request failed",
      status: response.status || 0,
    });
  }
}

function isProblem(value: unknown): value is Problem {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as Problem).type === "string" &&
    typeof (value as Problem).status === "number"
  );
}

interface FetchResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/** The body of a successful response, or an `ApiError` thrown for the caller to catch. */
export function unwrap<T>(result: FetchResult<T>): T {
  if (!result.response.ok) throw ApiError.from(result.error, result.response);
  return result.data as T;
}

/** For routes that answer 204 with no body, where `data` is legitimately undefined. */
export function unwrapEmpty(result: FetchResult<unknown>): void {
  if (!result.response.ok) throw ApiError.from(result.error, result.response);
}
